import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "../Logo";

const NAV_LINKS = [
  { to: "/plans", label: "Plans" },
  { to: "/guides", label: "Guides" },
  { to: "/demo", label: "Demo" },
  { to: "/contact", label: "Contact" },
] as const;

const DEMO_HREF = "/contact?purpose=demo";

function isActivePath(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

const headerVariants = {
  hidden: { y: -24, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring" as const, stiffness: 320, damping: 28, mass: 0.85 },
  },
};

const navItemVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 + i * 0.05, duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const mobileItemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.04 + i * 0.05, duration: 0.3, ease: "easeOut" as const },
  }),
};

export function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const linkClass = (href: string, mobile = false) => {
    const active = isActivePath(pathname, href);
    const base = mobile
      ? "block px-4 py-3 text-[15px] font-medium rounded-xl transition-colors"
      : "relative px-3.5 py-2 text-sm font-medium rounded-lg transition-colors";
    if (active) {
      return mobile ? `${base} text-brand-600 bg-brand-50` : `${base} text-brand-600`;
    }
    return mobile
      ? `${base} text-gray-700 hover:bg-gray-50`
      : `${base} text-gray-600 hover:text-gray-900 hover:bg-gray-50/80`;
  };

  return (
    <>
      <motion.header
        initial="hidden"
        animate="visible"
        variants={headerVariants}
        className={`fixed top-0 inset-x-0 z-50 transition-[box-shadow,background-color,border-color] duration-500 ${
          scrolled || menuOpen
            ? "bg-white/90 backdrop-blur-xl border-b border-gray-200/70 shadow-[0_1px_0_rgba(0,0,0,0.03),0_8px_30px_rgba(0,0,0,0.05)]"
            : "bg-white/70 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-[1fr_auto] lg:grid-cols-3 items-center h-[4.25rem] gap-4">
            {/* Logo — left column, aligns with page content edge */}
            <motion.div
              className="flex items-center justify-start min-w-0"
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05, duration: 0.4, ease: "easeOut" }}
            >
              <Link to="/" className="group shrink-0" aria-label="Cravbot home">
                <motion.div
                  className="flex items-center"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 18 }}
                >
                  <Logo size="sm" />
                </motion.div>
              </Link>
            </motion.div>

            {/* Nav — true center column on large screens */}
            <motion.nav
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.12, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:flex items-center justify-center justify-self-center gap-0.5 px-1.5 py-1 rounded-2xl bg-gray-50/90 border border-gray-200/60 shadow-sm"
              aria-label="Main"
            >
              {NAV_LINKS.map(({ to, label }, i) => (
                <motion.div
                  key={to}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={navItemVariants}
                >
                  <Link to={to} className={linkClass(to)}>
                    {label}
                    {isActivePath(pathname, to) && (
                      <motion.span
                        layoutId="nav-active-pill"
                        className="absolute inset-0 rounded-lg bg-brand-50 ring-1 ring-brand-100/80 -z-10"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                </motion.div>
              ))}
            </motion.nav>

            {/* Right column — mirrors left for balanced centering */}
            <div className="flex items-center justify-end justify-self-end gap-2 min-w-0">
            <motion.div
              className="hidden md:flex items-center shrink-0"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.18, duration: 0.4, ease: "easeOut" }}
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to={DEMO_HREF}
                  className="group relative inline-flex items-center gap-2 overflow-hidden px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-xl btn-glow transition-colors"
                >
                  <motion.span
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                    initial={{ x: "-120%" }}
                    whileHover={{ x: "120%" }}
                    transition={{ duration: 0.55, ease: "easeInOut" }}
                  />
                  <span className="relative">Book demo</span>
                  <svg
                    className="relative w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </motion.div>
            </motion.div>

            <motion.button
              type="button"
              className="md:hidden p-2.5 -mr-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              whileTap={{ scale: 0.92 }}
            >
              <motion.svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                animate={{ rotate: menuOpen ? 90 : 0 }}
                transition={{ duration: 0.25 }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </motion.svg>
            </motion.button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden border-t border-gray-100 bg-white/95 backdrop-blur-xl overflow-hidden"
            >
              <nav
                className="px-4 py-4 space-y-1 max-h-[calc(100vh-4.25rem)] overflow-y-auto scrollbar-modern"
                aria-label="Mobile"
              >
                {NAV_LINKS.map(({ to, label }, i) => (
                  <motion.div
                    key={to}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                    variants={mobileItemVariants}
                  >
                    <Link to={to} className={linkClass(to, true)}>
                      {label}
                    </Link>
                  </motion.div>
                ))}
                <div className="h-px bg-gray-100 my-3" />
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.24 }}
                >
                  <Link
                    to={DEMO_HREF}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-xl btn-glow transition-colors"
                  >
                    Book demo
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-gray-900/25 backdrop-blur-[2px] md:hidden"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
